import { useCallback, useContext, useEffect, useState, useMemo, useRef } from 'react';
import { I18nContext, I18nModel } from '@bingads-webui/copilot-i18n';
import { resumeConversation, deleteConversation, renameConversation } from '@bingads-webui-react/copilot-chat-history-data';
import { useConversationHistoryContext } from '../conversation-history-context';
import { useUpdateConversations } from './use-update-conversations';
import { useChatHistoryPerfMarkers } from './use-chat-history-perf-markers';
import type {
  ChatHistoryControllerProps,
  ChatHistoryControllerResult,
  ConversationResponse,
  DeleteConversationResponse,
  Conversation,
} from '../types';

export const useChatHistoryController = ({
  uid,
  cid,
  logger,
  activeConversationId,
  onConversationClick: onClick,
  onDeleteActiveConversation,
  appPerfMarker,
}: ChatHistoryControllerProps): ChatHistoryControllerResult => {
  const {
    deletePerfMarker,
    renamePerfMarker,
    resumePerfMarker,
  } = useChatHistoryPerfMarkers(appPerfMarker);

  const i18n = useContext(I18nContext) as I18nModel;
  const { setResumedConversationId, productName, getAuthToken } = useConversationHistoryContext();
  const {
    conversations,
    hasError,
    setHasError,
    loadMore,
    setConversations,
    hasBeenCalled: useUpdateConversationsHasBeenCalled,
  } = useUpdateConversations({
    uid, cid, logger, appPerfMarker, productName, getAuthToken,
  });

  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [isResumeConvInProgress, setIsResumeConvInProgress] = useState(false);
  const a11yAnnounceArea = useMemo(() => document.getElementById('a11yAnnounceArea'), []);

  const isMounted = useRef(true);

  // Cleanup on unmount
  useEffect(() => () => {
    isMounted.current = false;
  }, []);

  const onConversationClick = useCallback(async (conversationId: string) => {
    logger.trace(`[Chat History] Clicked to resume conversation, previously: ${conversationId}`);

    if (isResumeConvInProgress) {
      return;
    }

    setIsResumeConvInProgress(true);
    try {
      resumePerfMarker?.willInit();
      resumePerfMarker?.willFetchData();

      const isXandrProduct = productName === 'xandr_curate' || productName === 'xandr_monetization';
      const authToken = isXandrProduct && getAuthToken ? await getAuthToken() : undefined;

      const response = await resumeConversation({
        cid, uid, conversationId, productName, authToken,
      }) as ConversationResponse;

      if (response.hasError) {
        setHasError(true);
        logger.error(`[Chat History] Error while resuming conversation: ${response.message} ${JSON.stringify(response.error)} conversationId: ${conversationId}`);
        return;
      }
      if (response.success && response.token && response.conversationId) {
        resumePerfMarker?.willRender();
        setResumedConversationId(response.conversationId);
        await onClick(response.token);
        logger.trace(`[Chat History] Resumed conversation: ${response.conversationId}, previously: ${conversationId}`);
      } else {
        setHasError(true);
        logger.error(`[Chat History] Resume returned 200 but missing required fields. success: ${response.success}, token: ${!!response.token}, conversationId: ${response.conversationId}, original: ${conversationId}`);
      }
    } catch (e) {
      logger.error(`[Chat History] Error while resuming conversation in catch: ${JSON.stringify(e)} conversationId: ${conversationId}`);
      setHasError(true);
    } finally {
      resumePerfMarker?.done();
      // Only update state if component is still mounted
      if (isMounted.current) {
        setIsResumeConvInProgress(false);
      }
    }
  }, [cid, uid, isResumeConvInProgress, onClick, logger, setHasError, setResumedConversationId, resumePerfMarker, productName, getAuthToken]);

  const onConversationDelete = useCallback(async (conversationId: string) => {
    logger.trace(`[Chat History] Clicked confirm delete: ${conversationId}`);

    deletePerfMarker?.willInit();
    deletePerfMarker?.willFetchData();

    const isXandrProduct = productName === 'xandr_curate' || productName === 'xandr_monetization';
    const authToken = isXandrProduct && getAuthToken ? await getAuthToken() : undefined;

    deleteConversation({
      cid,
      uid,
      conversationId,
      productName,
      authToken,
    })
      .then((response: DeleteConversationResponse) => {
        if (response.hasError) {
          setHasError(true);
          logger.error(`[Chat History] Error while deleting conversation: ${response.message} ${JSON.stringify(response.error)}`);
          deletePerfMarker?.done();
          return;
        }
        deletePerfMarker?.willRender();
        setConversations(prev => prev.filter((c: Conversation) => c.ConversationId !== conversationId));
        logger.trace(`[Chat History] Deleted conversation: ${conversationId}`);
        deletePerfMarker?.done();

        if (activeConversationId === conversationId) {
          onDeleteActiveConversation();
        }

        if (a11yAnnounceArea) {
          a11yAnnounceArea.textContent = i18n.getString(_TL_('Conversation deleted'));
        }
      })
      .catch((e: { message?: string; error?: unknown }) => {
        setHasError(true);
        logger.error(`${e.message} ${JSON.stringify(e.error)}`);
        deletePerfMarker?.done();
      });
  }, [cid, uid, logger, setHasError, setConversations, activeConversationId, onDeleteActiveConversation, i18n, a11yAnnounceArea, deletePerfMarker, productName, getAuthToken]);

  const onConversationRename = useCallback(async (conversationId: string, newTitle: string) => {
    renamePerfMarker?.willInit();
    renamePerfMarker?.willFetchData();

    const isXandrProduct = productName === 'xandr_curate' || productName === 'xandr_monetization';
    const authToken = isXandrProduct && getAuthToken ? await getAuthToken() : undefined;

    renameConversation({
      cid,
      uid,
      conversationId,
      newTitle,
      productName,
      authToken,
    })
      .then((response: DeleteConversationResponse) => {
        if (response.hasError) {
          setHasError(true);
          logger.error(`${response.message} ${JSON.stringify(response.error)}`);
          renamePerfMarker?.done();
          return;
        }
        renamePerfMarker?.willRender();
        setConversations(prev => prev.map((c: Conversation) =>
          (c.ConversationId === conversationId ? { ...c, Title: newTitle } : c)));
        logger.trace(`Renamed conversation: ${conversationId} to ${newTitle}`);
        renamePerfMarker?.done();

        if (a11yAnnounceArea) {
          a11yAnnounceArea.textContent = i18n.getString(_TL_('Renamed conversation from {{conversationId}} to {{newTitle}}'), { conversationId, newTitle });
        }
      })
      .catch((e: { message?: string; error?: unknown }) => {
        setHasError(true);
        logger.error(`${e.message} ${JSON.stringify(e.error)}`);
        renamePerfMarker?.done();
      });
  }, [cid, uid, logger, setHasError, setConversations, i18n, a11yAnnounceArea, renamePerfMarker, productName, getAuthToken]);

  useEffect(() => {
    if (isFirstLoad && useUpdateConversationsHasBeenCalled) {
      setIsFirstLoad(false);
    }
  }, [isFirstLoad, useUpdateConversationsHasBeenCalled]);

  return {
    conversations,
    hasError,
    setHasError,
    isFirstLoad,
    isResumeConvInProgress,
    onConversationClick,
    onConversationDelete,
    onConversationRename,
    loadMore,
  };
};
